import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { RootState } from '../../ApplicationState';
import { selectClassroom } from '../../Classrooms';
import { Segment, showRightArrowWhiteIcon } from '../../core/core';
import Button from '../Button';
import { Icon, IconifyIcon } from '@iconify/react';
import cx from 'classnames';
import segmentStyles from '../../SCSS/Segment.module.scss';
import utilityStyles from '../../SCSS/common/utilityStyles.module.scss';
import styles from './ReportsDashboardGallery.module.scss';

type Props = {
  categories: { [key: string]: GalleryCategory };
  isTakweenWellbeingLead?: boolean;
};

interface GalleryCategory {
  representativeImage: string | IconifyIcon;
  onClickUrl: string;
  title: string;
  gradeSpan?: number[];
  requiredKeys?: string[];
  restrictedKeys?: string[];
  category: string;
  description: string;
}

const GalleryCard = ({
  representativeImage,
  onClickUrl,
  title,
  description,
}: Partial<GalleryCategory>): JSX.Element => {
  const history = useHistory();
  return (
    <div className={styles.galleryCard} key={title}>
      {representativeImage && (
        <div className={styles.iconAlign}>
          <Icon className={styles.icon} icon={representativeImage} />
        </div>
      )}
      <div className={styles.titleColumn}>
        <h3>{title}</h3>
        <p>{description}</p>
      </div>
      {onClickUrl && (
        <div className={styles.buttonAlign}>
          <Button xSmall onClick={() => history.push(onClickUrl)}>
            <span>View</span>
            <span className={styles.arrow}>{showRightArrowWhiteIcon()}</span>
          </Button>
        </div>
      )}
    </div>
  );
};

const ReportsDashboardGallery = ({ categories, isTakweenWellbeingLead }: Props): JSX.Element => {
  const classroom = useSelector((state: RootState) => {
    const selectedClassroom = selectClassroom(state);
    return selectedClassroom;
  });
  const keyRing = useSelector((state: RootState) => {
    return state.user.userProfile!.keyRing || null;
  });
  const teacherReports = Object.entries(categories).filter((category) => category[1].category === 'Teacher');
  const adminReports = Object.entries(categories).filter((category) => category[1].category === 'Admin');
  const resellerReports = Object.entries(categories).filter((category) => category[1].category === 'Reseller');
  return (
    <div className={styles.reportsDashboardGalleryContainer}>
      <Segment className={cx(utilityStyles.line, styles.line)}>
        <div className={styles.titleLine}>
          <p className={segmentStyles.moduleTitle}>Reports</p>
        </div>
      </Segment>
      <div className={styles.gallery}>
        <div className={styles.column}>
          <div className={styles.title}>Teachers</div>
          <div className={styles.galleryCards}>
            {teacherReports.map((category) => {
              const { requiredKeys, gradeSpan, title, representativeImage, onClickUrl, description } = category[1];
              if (
                (gradeSpan && parseInt(classroom?.grade) < gradeSpan[0]) ||
                (gradeSpan && parseInt(classroom?.grade) > gradeSpan[1])
              )
                return null;
              return requiredKeys ? (
                requiredKeys.reduce((acc, key) => (!acc ? false : keyRing.includes(key)), true) && (
                  <GalleryCard
                    key={onClickUrl}
                    title={title}
                    representativeImage={representativeImage}
                    onClickUrl={onClickUrl}
                    description={description}
                  />
                )
              ) : (
                <GalleryCard
                  key={onClickUrl}
                  title={title}
                  representativeImage={representativeImage}
                  onClickUrl={onClickUrl}
                  description={description}
                />
              );
            })}
          </div>
        </div>
        <div className={styles.adminAndReseller}>
          {keyRing.includes('KEY_ADMINISTRATOR_REPORTS') || isTakweenWellbeingLead ? (
            <div className={styles.column}>
              <div className={styles.title}>Administrators</div>
              <div className={styles.galleryCards}>
                {adminReports.map((category) => {
                  const {
                    requiredKeys,
                    restrictedKeys,
                    gradeSpan,
                    title,
                    representativeImage,
                    onClickUrl,
                    description,
                  } = category[1];
                  if (
                    (gradeSpan && parseInt(classroom?.grade) < gradeSpan[0]) ||
                    (gradeSpan && parseInt(classroom?.grade) > gradeSpan[1])
                  )
                    return null;
                  return requiredKeys && restrictedKeys ? (
                    [...requiredKeys, ...restrictedKeys].reduce(
                      (acc, key) => (acc ? false : !keyRing.includes(key)),
                      true,
                    ) && (
                      <GalleryCard
                        key={onClickUrl}
                        title={title}
                        representativeImage={representativeImage}
                        onClickUrl={onClickUrl}
                        description={description}
                      />
                    )
                  ) : requiredKeys && !restrictedKeys ? (
                    requiredKeys.reduce((acc, key) => (!acc ? false : keyRing.includes(key)), true) && (
                      <GalleryCard
                        key={onClickUrl}
                        title={title}
                        representativeImage={representativeImage}
                        onClickUrl={onClickUrl}
                        description={description}
                      />
                    )
                  ) : (
                    <GalleryCard
                      key={onClickUrl}
                      title={title}
                      representativeImage={representativeImage}
                      onClickUrl={onClickUrl}
                      description={description}
                    />
                  );
                })}
              </div>
            </div>
          ) : (
            <div className={styles.column} />
          )}
          {keyRing.includes('KEY_RESELLER_REPORTS') ? (
            <div className={styles.column}>
              <div className={styles.title}>Resellers</div>
              <div className={styles.galleryCards}>
                {resellerReports.map((category) => {
                  const { requiredKeys, gradeSpan, title, representativeImage, onClickUrl, description } = category[1];
                  if (
                    (gradeSpan && parseInt(classroom?.grade) < gradeSpan[0]) ||
                    (gradeSpan && parseInt(classroom?.grade) > gradeSpan[1])
                  )
                    return null;
                  return requiredKeys ? (
                    requiredKeys.reduce((acc, key) => (!acc ? false : keyRing.includes(key)), true) && (
                      <GalleryCard
                        key={onClickUrl}
                        title={title}
                        representativeImage={representativeImage}
                        onClickUrl={onClickUrl}
                        description={description}
                      />
                    )
                  ) : (
                    <GalleryCard
                      key={onClickUrl}
                      title={title}
                      representativeImage={representativeImage}
                      onClickUrl={onClickUrl}
                      description={description}
                    />
                  );
                })}
              </div>
            </div>
          ) : (
            <div className={styles.column} />
          )}
        </div>
      </div>
    </div>
  );
};

export default ReportsDashboardGallery;
